import { useCallback, useEffect, useMemo, useState } from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';

type RefreshBehavior =
  | 'collapse_all'
  | 'autoexpand_first'
  | 'preserve_expanded';

export function useAccordionState(
  keys: string[],
  defaultExpandFirst: boolean = true,
  refreshBehavior: RefreshBehavior = 'collapse_all',
): {
  expandedIndices: number[];
  handleExpandChange: (expandedIndices: number[]) => void;
  resetAccordionState: () => void;
} {
  const translateIndicesToKeys = useCallback(
    (indices: number[]) =>
      indices.reduce<Set<string>>((result, idx) => {
        if (keys.length > idx) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          result.add(keys[idx]!);
        }
        return result;
      }, new Set()),
    [keys],
  );
  const translateKeysToIndices = useCallback(
    (translateKeys: Set<string>) =>
      Array.from(translateKeys).reduce<number[]>((result, key) => {
        const idx = keys.indexOf(key);
        if (idx !== -1) {
          result.push(idx);
        }
        return result;
      }, []),
    [keys],
  );
  const [touched, setTouched] = useState(false);
  const defaultExpanded = useMemo(
    () => (defaultExpandFirst ? [0] : []),
    [defaultExpandFirst],
  );
  const [expanded, setExpanded] = useState<number[]>(defaultExpanded);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    translateIndicesToKeys(defaultExpanded),
  );

  const handleExpandChange = useCallback(
    (expandedIndices: number[]) => {
      setExpanded(expandedIndices);
      setTouched(true);

      if (refreshBehavior === 'preserve_expanded') {
        setExpandedKeys(translateIndicesToKeys(expandedIndices));
      }
    },
    [refreshBehavior, translateIndicesToKeys],
  );
  const resetAccordionState = useCallback(() => {
    switch (refreshBehavior) {
      case 'autoexpand_first':
        setExpanded(defaultExpanded);
        break;
      case 'collapse_all':
        if (keys.length === 1 || !touched) {
          // If there is only one item in the list, expand it.
          // Until the user has collapsed / expanded something, continue
          // to auto-expand the first item in the list.
          setExpanded(defaultExpanded);
        } else {
          setExpanded([]);
        }
        break;
      case 'preserve_expanded':
        if (!touched && defaultExpandFirst) {
          setExpanded(defaultExpanded);
          setExpandedKeys(translateIndicesToKeys(defaultExpanded));
        }
        break;
      default:
        assertUnreachable(refreshBehavior);
    }
  }, [
    defaultExpandFirst,
    defaultExpanded,
    keys.length,
    refreshBehavior,
    touched,
    translateIndicesToKeys,
  ]);
  useEffect(() => {
    // reset IFF keys change
    resetAccordionState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys]);

  return {
    expandedIndices:
      refreshBehavior === 'preserve_expanded'
        ? translateKeysToIndices(expandedKeys)
        : expanded,
    handleExpandChange,
    resetAccordionState,
  };
}
